import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/movies';
import { useFavorites } from '@/hooks/useFavorites';

const navItems = [
  { label: '首页', to: '/' },
  { label: '分类', to: '/category/drama' },
  { label: '资讯', to: '/news' },
  { label: '搜索', to: '/search' },
  { label: '关于', to: '/about' },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();
  const { count } = useFavorites();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || mobileOpen
          ? 'border-b border-background-800 bg-background-950/95 backdrop-blur-md'
          : 'border-b border-transparent bg-gradient-to-b from-background-950/85 via-background-950/40 to-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 max-w-content items-center justify-between gap-4 px-4 md:h-20 md:px-6">
        <Link to="/" className="group flex cursor-pointer items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-600 text-base font-bold text-background-50 transition-colors group-hover:bg-primary-500">
            日
          </span>
          <span className="flex flex-col leading-none">
            <span className="text-[15px] font-bold tracking-tight text-foreground-50">日韩成人电影</span>
            <span className="mt-1 font-label text-[10px] uppercase tracking-[0.26em] text-foreground-500">
              J &amp; K Cinema Review
            </span>
          </span>
          <span className="ml-1 rounded border border-accent-500/60 px-1.5 py-0.5 font-label text-[10px] font-semibold tracking-wider text-accent-400">
            18+
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) =>
            item.label === '分类' ? (
              <div
                key={item.to}
                className="relative"
                onMouseEnter={() => setCategoryOpen(true)}
                onMouseLeave={() => setCategoryOpen(false)}
              >
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                      isActive ? 'text-accent-400' : 'text-foreground-300 hover:text-foreground-50'
                    }`
                  }
                >
                  {item.label}
                  <i className={`ri-arrow-down-s-line text-sm transition-transform ${categoryOpen ? 'rotate-180' : ''}`} />
                </NavLink>
                <div
                  className={`absolute left-1/2 top-full w-[420px] -translate-x-1/2 pt-3 transition-all duration-200 ${
                    categoryOpen ? 'visible opacity-100' : 'invisible opacity-0'
                  }`}
                >
                  <div className="rounded-lg border border-background-800 bg-background-900/95 p-3 backdrop-blur-md">
                    <div className="grid grid-cols-2 gap-1">
                      {categories.map((category) => (
                        <Link
                          key={category.slug}
                          to={`/category/${category.slug}`}
                          className="group flex cursor-pointer items-start gap-2.5 rounded-md px-2.5 py-2 transition-colors hover:bg-background-800"
                        >
                          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded bg-primary-950 text-primary-300 transition-colors group-hover:bg-primary-600 group-hover:text-background-50">
                            <i className={`${category.icon} text-sm`} />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm text-foreground-200 group-hover:text-foreground-50">
                              {category.name}
                            </span>
                            <span className="block truncate font-label text-[10px] uppercase tracking-[0.14em] text-foreground-600">
                              {category.en}
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>
                    <div className="mt-2 border-t border-background-800 pt-2">
                      <Link
                        to="/search"
                        className="flex cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-xs text-foreground-400 transition-colors hover:text-accent-400"
                      >
                        浏览全部影片与标签
                        <i className="ri-arrow-right-line text-sm" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive ? 'text-accent-400' : 'text-foreground-300 hover:text-foreground-50'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ),
          )}
        </nav>

        <div className="flex items-center gap-1.5 md:gap-2">
          <Link
            to="/search"
            aria-label="搜索"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-300 transition-colors hover:bg-background-800 hover:text-foreground-50"
          >
            <i className="ri-search-line text-lg" />
          </Link>
          <Link
            to="/favorites"
            aria-label="我的收藏"
            className="relative flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-300 transition-colors hover:bg-background-800 hover:text-foreground-50"
          >
            <i className="ri-heart-line text-lg" />
            {count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 font-label text-[10px] font-bold text-background-950">
                {count}
              </span>
            )}
          </Link>
          <div className="mx-1 hidden h-5 w-px bg-background-700 md:block" />
          <Link
            to="/news"
            className="hidden cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
          >
            最新资讯
          </Link>
          <button
            type="button"
            aria-label="打开菜单"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((prev) => !prev)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-200 transition-colors hover:bg-background-800 lg:hidden"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
          </button>
        </div>
      </div>

      <div
        className={`overflow-hidden border-t border-background-800 bg-background-950/95 transition-[max-height] duration-300 lg:hidden ${
          mobileOpen ? 'max-h-[80vh] overflow-y-auto' : 'max-h-0 border-t-0'
        }`}
      >
        <div className="mx-auto max-w-content px-4 py-4 md:px-6">
          <div className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center justify-between rounded-md px-3 py-3 text-sm ${
                    isActive ? 'bg-primary-950 text-accent-400' : 'text-foreground-300'
                  }`
                }
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-base text-foreground-600" />
              </NavLink>
            ))}
            <Link
              to="/favorites"
              className="flex items-center justify-between rounded-md px-3 py-3 text-sm text-foreground-300"
            >
              我的收藏
              <span className="font-label text-xs text-accent-500">{count} 部</span>
            </Link>
          </div>

          <div className="mt-3 border-t border-background-800 pt-4">
            <p className="mb-2 font-label text-[11px] uppercase tracking-[0.24em] text-foreground-600">影片分类</p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((category) => (
                <Link
                  key={category.slug}
                  to={`/category/${category.slug}`}
                  className="flex cursor-pointer items-center gap-2 rounded-md border border-background-800 bg-background-900 px-3 py-2.5 text-xs text-foreground-300"
                >
                  <i className={`${category.icon} text-sm text-primary-300`} />
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}